import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@mlm/ui';
import {
  FUNDS_ACCOUNT_KINDS,
  FUNDS_ACCOUNT_WRITE_PERM,
  isBlockedFundsCounterCode,
} from '@mlm/shared';
import {
  type FundsAccount,
  type FundsAccountKindValue,
  type FundsMappingRow,
  apiErrorPayload,
  createFundsAccount,
  createFundsDocument,
  createFundsMapping,
  deleteFundsMapping,
  fetchFundsRegister,
  listBranches,
  listFundsAccounts,
  listGlAccounts,
  listFundsDocuments,
  listFundsMappings,
  updateFundsAccount,
  updateFundsMapping,
  voidFundsDocument,
} from '../../lib/api';
import { downloadFundsRegister, type ReportExportMeta } from '../../lib/report-export';
import { ReportExportButtons } from '../../components/report-export-buttons';
import { useHasPerm } from '../../lib/auth-store';
import { Money } from './_shared';

/**
 * Cash & Bank — the company's registered cash, bank, cheque and e-wallet
 * accounts, their balances, and the money that moved through them.
 *
 * SCOPE, stated on the page: expenses, supplier payments and consignment
 * receipts post from their own screens with a user-picked account. Every
 * headless movement — POS tenders, gateway / manual / COD payments, top-ups,
 * cash-outs — posts automatically once an auto-posting rule
 * (`funds_account_mappings`: (source, code, branch) → account, managed from
 * this page under `funds_account.write`) names the account. No rule = silent
 * skip, nothing backfilled. Shift close is still the drawer COUNT; a till's
 * register balance is its book balance — compared, never merged.
 */

const KIND_LABEL: Record<FundsAccountKindValue, string> = {
  cash: 'Cash',
  bank: 'Bank',
  cheque: 'Cheque',
  ewallet: 'E-wallet',
};

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const monthStart = () => `${todayStr().slice(0, 7)}-01`;
/** Local day bounds — a date-only string would parse as UTC midnight (8 AM Manila). */
const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const dayEnd = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();

export function FundsPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm(FUNDS_ACCOUNT_WRITE_PERM);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<FundsAccount | null | 'new'>(null);
  const [docKind, setDocKind] = useState<null | 'deposit' | 'withdrawal' | 'transfer'>(null);
  const [historyFor, setHistoryFor] = useState<FundsAccount | null>(null);
  const [showMappings, setShowMappings] = useState(false);

  const accountsQ = useQuery({
    queryKey: ['funds-accounts'],
    queryFn: () => listFundsAccounts(),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['funds-accounts'] });
    void qc.invalidateQueries({ queryKey: ['funds-register'] });
    void qc.invalidateQueries({ queryKey: ['funds-documents'] });
  };

  const all = accountsQ.data?.rows ?? [];
  const rows = showInactive ? all : all.filter((a) => a.active);
  const totals = accountsQ.data?.totalsByKind ?? {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Cash &amp; Bank</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Company accounts money moves through. Sales, online and manual payments, COD
            collections, top-ups and cash-outs post here automatically once an auto-posting rule
            names the account; expenses, supplier payments and consignment receipts post from their
            own screens. Shift close remains the drawer count — the till account is its book
            balance.
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="mr-1 h-4 w-4" /> New account
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDocKind('deposit')}>
              Record Deposit
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDocKind('withdrawal')}>
              Record Withdrawal
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDocKind('transfer')}>
              Transfer
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowMappings(true)}>
              Auto-posting rules
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FUNDS_ACCOUNT_KINDS.map((kind) => (
          <Card key={kind}>
            <CardContent className="pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {KIND_LABEL[kind]}
              </p>
              <p className="mt-1 text-xl font-semibold">
                {/* Summed server-side in Decimal — never added up here. */}
                <Money>{peso(totals[kind] ?? '0')}</Money>
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Accounts</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => setShowInactive((v) => !v)}>
            {showInactive ? 'Hide inactive' : 'Show inactive'}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {accountsQ.isLoading ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              No accounts registered yet. Add the cash, bank and cheque accounts your business
              actually uses, then pick one when recording a payment, an expense or a collection.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">Account</th>
                    <th className="px-4 py-2">Kind</th>
                    <th className="px-4 py-2">Bank / No.</th>
                    <th className="px-4 py-2">Branch</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="border-b last:border-0">
                      <td className="px-4 py-2">
                        <span className="font-medium">{a.name}</span>
                        {a.code ? (
                          <span className="ml-1 font-mono text-xs text-muted-foreground">
                            {a.code}
                          </span>
                        ) : null}
                        {!a.active && (
                          <Badge variant="secondary" className="ml-2">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2">{KIND_LABEL[a.kind]}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {[a.bankName, a.accountNumber].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{a.branchName ?? '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <Money>
                          <span className={Number(a.currentBalance) < 0 ? 'text-destructive' : ''}>
                            {peso(a.currentBalance)}
                          </span>
                        </Money>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setHistoryFor(a)}>
                            History
                          </Button>
                          {canWrite && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                              Edit
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing !== null && (
        <AccountDialog
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {docKind && (
        <FundsDocDialog
          kind={docKind}
          accounts={all.filter((a) => a.active)}
          onClose={() => setDocKind(null)}
          onDone={() => {
            setDocKind(null);
            refresh();
          }}
        />
      )}
      {historyFor && (
        <AccountHistory
          account={historyFor}
          canWrite={canWrite}
          onClose={() => setHistoryFor(null)}
          onChanged={refresh}
        />
      )}
      {showMappings && canWrite && (
        <MappingsDialog accounts={all} onClose={() => setShowMappings(false)} />
      )}
    </div>
  );
}

// ── Account create / edit ───────────────────────────────────────────────────

function AccountDialog({
  account,
  onClose,
  onDone,
}: {
  account: FundsAccount | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const isNew = !account;
  const [name, setName] = useState(account?.name ?? '');
  const [code, setCode] = useState(account?.code ?? '');
  const [kind, setKind] = useState<FundsAccountKindValue>(account?.kind ?? 'bank');
  const [bankName, setBankName] = useState(account?.bankName ?? '');
  const [accountNumber, setAccountNumber] = useState(account?.accountNumber ?? '');
  const [branchId, setBranchId] = useState(account?.branchId ?? '');
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingDate, setOpeningDate] = useState(todayStr());
  const [active, setActive] = useState(account?.active ?? true);
  const [memo, setMemo] = useState(account?.memo ?? '');

  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });

  const submit = useMutation({
    mutationFn: async () => {
      const common = {
        name: name.trim(),
        code: code.trim() || null,
        kind,
        bankName: bankName.trim() || null,
        accountNumber: accountNumber.trim() || null,
        branchId: branchId || null,
        memo: memo.trim() || null,
      };
      if (isNew) {
        return createFundsAccount({
          ...common,
          openingBalance: openingBalance.trim() || null,
          openingBalanceDate: openingBalance.trim() ? dayStart(openingDate) : null,
        });
      }
      return updateFundsAccount(account.id, { ...common, active });
    },
    onSuccess: () => {
      toast.success(isNew ? 'Account registered' : 'Account updated');
      onDone();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'failed';
      toast.error(
        msg === 'funds_account_name_taken'
          ? 'Another account already uses that name.'
          : msg === 'funds_account_code_taken'
            ? 'Another account already uses that code.'
            : msg,
      );
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? 'New account' : 'Edit account'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="BDO Current Account"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Kind</label>
              <select
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value as FundsAccountKindValue)}
              >
                {FUNDS_ACCOUNT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Short code (optional)</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="BDO-01" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Bank (optional)</label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Account no. (optional)</label>
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="•••• 0012"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              Branch (a cash till belongs to one; leave blank for company-wide)
            </label>
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">— none —</option>
              {(branchesQ.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          {isNew ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Opening balance</label>
                <MoneyInput
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">As of</label>
                <Input
                  type="date"
                  value={openingDate}
                  onChange={(e) => setOpeningDate(e.target.value)}
                />
              </div>
              <p className="col-span-2 text-xs text-muted-foreground">
                Recorded as the account&apos;s first transaction, so its history always adds up to
                its balance. It can&apos;t be edited afterwards — correct it with a deposit or
                withdrawal.
              </p>
            </div>
          ) : (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              Active (inactive accounts can&apos;t receive new transactions)
            </label>
          )}
          <div>
            <label className="text-xs text-muted-foreground">Memo (optional)</label>
            <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? 'Saving…' : isNew ? 'Register account' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Manual document ─────────────────────────────────────────────────────────

function FundsDocDialog({
  kind,
  accounts,
  onClose,
  onDone,
}: {
  kind: 'deposit' | 'withdrawal' | 'transfer';
  accounts: FundsAccount[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [fromAccountId, setFrom] = useState('');
  const [toAccountId, setTo] = useState('');
  const [occurredAt, setOccurredAt] = useState(todayStr());
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [counterCode, setCounterCode] = useState('');
  // Stable for the life of the dialog: re-submitting the same document must
  // replay rather than move the money twice.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  // A deposit/withdrawal MUST name the GL account for its OTHER side
  // (owner's capital, a loan, a one-off income) — the route rejects it
  // otherwise. Transfers never need one — both legs are cash.
  const glAccountsQ = useQuery({
    queryKey: ['gl-accounts'],
    queryFn: listGlAccounts,
    enabled: kind !== 'transfer',
  });
  // Same predicate the server enforces — a local approximation used to offer
  // codes (2100/2120/2130/2310) that then 400 as `counter_account_not_allowed`.
  const counterOptions = (glAccountsQ.data?.rows ?? []).filter(
    (a) => a.active && !isBlockedFundsCounterCode(a.code),
  );

  // No branch field: a manual document carries none, and each transfer leg is
  // validated against its OWN account's branch server-side — so till-to-till
  // and till-to-bank transfers both work without one.
  const needsFrom = kind !== 'deposit';
  const needsTo = kind !== 'withdrawal';
  const title =
    kind === 'deposit'
      ? 'Record deposit'
      : kind === 'withdrawal'
        ? 'Record withdrawal'
        : 'Transfer';

  const submit = useMutation({
    mutationFn: () =>
      createFundsDocument({
        kind,
        amount,
        fromAccountId: needsFrom ? fromAccountId : null,
        toAccountId: needsTo ? toAccountId : null,
        occurredAt: dayStart(occurredAt),
        reference: reference.trim() || null,
        memo: memo.trim() || null,
        counterGlAccountCode: kind !== 'transfer' && counterCode ? counterCode : null,
        clientRequestId,
      }),
    onSuccess: ({ document }) => {
      toast.success(`${title} ${document.docNo} recorded`);
      onDone();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'failed';
      toast.error(
        msg === 'same_account_transfer'
          ? 'Pick two different accounts.'
          : msg === 'counter_gl_account_required'
            ? kind === 'deposit'
              ? 'Pick where the money came from in the books.'
              : 'Pick where the money went to in the books.'
            : msg,
      );
    },
  });

  const valid =
    Number(amount) > 0 &&
    (!needsFrom || !!fromAccountId) &&
    (!needsTo || !!toAccountId) &&
    (kind !== 'transfer' || fromAccountId !== toAccountId) &&
    (kind === 'transfer' || !!counterCode);

  const picker = (value: string, onChange: (v: string) => void, exclude?: string) => (
    <select
      className="h-9 w-full rounded-md border bg-background px-2 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">— select —</option>
      {accounts
        .filter((a) => a.id !== exclude)
        .map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} ({KIND_LABEL[a.kind]})
          </option>
        ))}
    </select>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {needsFrom && (
            <div>
              <label className="text-xs text-muted-foreground">From account</label>
              {picker(fromAccountId, setFrom, kind === 'transfer' ? toAccountId : undefined)}
            </div>
          )}
          {needsTo && (
            <div>
              <label className="text-xs text-muted-foreground">To account</label>
              {picker(toAccountId, setTo, kind === 'transfer' ? fromAccountId : undefined)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Amount</label>
              <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Date</label>
              <Input
                type="date"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
              />
            </div>
          </div>
          {kind !== 'transfer' && (
            <div>
              <label className="text-xs text-muted-foreground">
                {kind === 'deposit' ? 'Money came from (books)' : 'Money went to (books)'}
              </label>
              {glAccountsQ.isLoading ? (
                <div className="flex h-9 items-center rounded-md border bg-background px-2 text-sm text-muted-foreground">
                  Loading accounts…
                </div>
              ) : counterOptions.length === 0 ? (
                <div className="flex h-9 items-center rounded-md border bg-background px-2 text-sm text-muted-foreground">
                  No active GL accounts to book against — check the Chart of Accounts.
                </div>
              ) : (
                <select
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={counterCode}
                  onChange={(e) => setCounterCode(e.target.value)}
                >
                  <option value="">— select —</option>
                  {counterOptions.map((a) => (
                    <option key={a.id} value={a.code}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground">Reference (optional)</label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Slip / cheque no."
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Memo (optional)</label>
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? 'Saving…' : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Per-account history ─────────────────────────────────────────────────────

function AccountHistory({
  account,
  canWrite,
  onClose,
  onChanged,
}: {
  account: FundsAccount;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [voidTarget, setVoidTarget] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const registerQ = useQuery({
    queryKey: ['funds-register', account.id, from, to],
    queryFn: () => fetchFundsRegister(account.id, { from: dayStart(from), to: dayEnd(to) }),
  });
  const docsQ = useQuery({
    queryKey: ['funds-documents', account.id],
    queryFn: () => listFundsDocuments({ accountId: account.id, limit: 100 }),
  });

  const doVoid = useMutation({
    mutationFn: () => voidFundsDocument(voidTarget!, reason.trim()),
    onSuccess: () => {
      toast.success('Document voided');
      setVoidTarget(null);
      setReason('');
      void registerQ.refetch();
      void docsQ.refetch();
      onChanged();
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'failed',
      ),
  });

  const report = registerQ.data;
  const summary = Object.fromEntries((report?.summary ?? []).map((s) => [s.label, s.value]));
  const registerRange = { from: dayStart(from), to: dayEnd(to) };
  // The account name rides in the title (and therefore the filename) — a
  // register export is meaningless without saying which account it is.
  const registerMeta: ReportExportMeta = {
    title: `Account Register — ${account.name}`,
    sheetName: 'Register',
    subtitle: [`Period: ${from} → ${to}`],
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{account.name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <ReportExportButtons
            result={report}
            meta={registerMeta}
            onCsv={() =>
              downloadFundsRegister(account.id, registerRange, 'csv', {
                ...registerMeta,
                result: report,
              })
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          {['Opening balance', 'Total in', 'Total out', 'Closing balance'].map((label) => (
            <div key={label} className="rounded-md border px-3 py-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="mt-0.5 font-medium">
                <Money>{peso(summary[label] ?? '0')}</Money>
              </p>
            </div>
          ))}
        </div>

        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Doc No</th>
                <th className="py-2 pr-3">Memo</th>
                <th className="py-2 pr-3 text-right">In</th>
                <th className="py-2 pr-3 text-right">Out</th>
                <th className="py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {(report?.rows ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                    Nothing moved through this account in that period.
                  </td>
                </tr>
              ) : (
                (report?.rows ?? []).map((r, i) => (
                  <tr key={`${String(r.doc_no)}-${i}`} className="border-b last:border-0">
                    <td className="py-2 pr-3">{String(r.date ?? '').slice(0, 10)}</td>
                    <td className="py-2 pr-3">{r.type}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.doc_no}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.memo ?? ''}</td>
                    <td className="py-2 pr-3 text-right">
                      <Money>{r.money_in ? peso(String(r.money_in)) : ''}</Money>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <Money>{r.money_out ? peso(String(r.money_out)) : ''}</Money>
                    </td>
                    <td className="py-2 text-right">
                      <Money>{peso(String(r.balance ?? '0'))}</Money>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {canWrite && (docsQ.data ?? []).length > 0 && (
          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
              Manual documents
            </p>
            <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {(docsQ.data ?? []).map((d) => (
                    <tr key={d.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 font-mono text-xs">{d.docNo}</td>
                      <td className="py-1.5 pr-3">{d.kind}</td>
                      <td className="py-1.5 pr-3 text-right">
                        <Money>{peso(d.amount)}</Money>
                      </td>
                      <td className="py-1.5 pr-3">
                        {d.status === 'void' ? (
                          <Badge variant="secondary">Void</Badge>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setVoidTarget(d.id)}>
                            Void
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {voidTarget && (
          <div className="rounded-md border p-3">
            <label className="text-xs text-muted-foreground">Reason for voiding</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setVoidTarget(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={reason.trim().length < 3 || doVoid.isPending}
                onClick={() => doVoid.mutate()}
              >
                Void document
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Ordered by document date, so a backdated entry lands where it belongs and the running
          balance always ends at the account&apos;s current balance.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Auto-posting rules ──────────────────────────────────────────────────────

/**
 * Server error slugs the rule editor handles inline instead of as a raw toast.
 * Everything else falls through to the MutationCache's default error toast.
 */
const MAPPING_FRIENDLY: Record<string, string> = {
  mapping_exists: 'A rule for that source, code and branch already exists.',
  funds_account_not_found: 'That account no longer exists — pick another.',
  funds_account_inactive: 'That account is inactive. Reactivate it or pick another.',
  funds_account_branch_mismatch:
    "That account belongs to a different branch than the rule's. Pick a company-wide account or one from the same branch.",
  branchless_source_requires_branchless_account:
    'Top-ups and cash-outs carry no branch, so this rule needs a company-wide account.',
  accounting_disabled: 'Accounting is turned off for this tenant.',
};
const MAPPING_SILENT_CODES = Object.keys(MAPPING_FRIENDLY);

function mappingErr(error: unknown): string | undefined {
  const code = apiErrorPayload(error)?.error;
  return code ? MAPPING_FRIENDLY[code] : undefined;
}

function MappingsDialog({ accounts, onClose }: { accounts: FundsAccount[]; onClose: () => void }) {
  const qc = useQueryClient();
  const mappingsQ = useQuery({ queryKey: ['funds-mappings'], queryFn: () => listFundsMappings() });
  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });

  const sources = mappingsQ.data?.sources ?? [];
  const rows = mappingsQ.data?.rows ?? [];
  const suggestionsBySource = mappingsQ.data?.codeSuggestions ?? {};

  // Add form. `source` stays '' until the catalog arrives, then defaults to
  // the first entry — no effect needed, the fallback is computed on render.
  const [sourcePick, setSourcePick] = useState('');
  const [anyCode, setAnyCode] = useState(true);
  const [code, setCode] = useState('');
  const [branchId, setBranchId] = useState('');
  const [fundsAccountId, setFundsAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const source = sourcePick || sources[0]?.source || '';
  const srcMeta = sources.find((s) => s.source === source);
  const branchless = srcMeta?.branchless ?? false;
  // A branchless source's documents carry no branch, so the rule can't either.
  const effectiveBranchId = branchless ? '' : branchId;
  const suggestions = suggestionsBySource[source] ?? [];

  // Accounts the register would accept for this (source, branch): company-wide
  // always; a branch-bound till only when the rule names that same branch.
  const eligibleAccounts = accounts.filter(
    (a) =>
      a.active &&
      (a.branchId === null ||
        (!branchless && !!effectiveBranchId && a.branchId === effectiveBranchId)),
  );
  const accountOk = eligibleAccounts.some((a) => a.id === fundsAccountId);
  const codeOk = anyCode || code.trim().length > 0;
  const canSubmit = !!source && accountOk && codeOk;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['funds-mappings'] });
    void qc.invalidateQueries({ queryKey: ['funds-accounts'] });
  };

  const createM = useMutation({
    mutationFn: () =>
      createFundsMapping({
        source,
        code: anyCode ? null : code.trim(),
        branchId: effectiveBranchId || null,
        fundsAccountId,
        memo: memo.trim() || null,
      }),
    meta: { successMessage: 'Rule added.', silentErrorCodes: MAPPING_SILENT_CODES },
    onSuccess: () => {
      invalidate();
      // Keep source / branch / account: the next rule is usually a sibling.
      setCode('');
      setAnyCode(true);
      setMemo('');
    },
  });
  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { active?: boolean } }) =>
      updateFundsMapping(id, patch),
    meta: { successMessage: 'Rule saved.', silentErrorCodes: MAPPING_SILENT_CODES },
    // Settled, not success: a 404 means another admin removed the rule, and
    // the list must resync rather than keep offering a row that is gone.
    onSettled: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => deleteFundsMapping(id),
    meta: { successMessage: 'Rule removed.' },
    onSettled: () => {
      invalidate();
      setDeleteId(null);
    },
  });

  const busy = createM.isPending || updateM.isPending || deleteM.isPending;
  const labelFor = (s: string) => sources.find((x) => x.source === s)?.label ?? s;
  const directionFor = (s: string) =>
    sources.find((x) => x.source === s)?.direction === 'out' ? 'money out' : 'money in';

  // Rows arrive sorted by source (specific before wildcard); group headers
  // are emitted on each source change so the order is the resolver's.
  const grouped: Array<{ source: string; rules: FundsMappingRow[] }> = [];
  for (const r of rows) {
    const last = grouped[grouped.length - 1];
    if (last && last.source === r.source) last.rules.push(r);
    else grouped.push({ source: r.source, rules: [r] });
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Auto-posting rules</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Rules apply to movements recorded after the rule exists. Nothing is backfilled. The most
            specific rule wins: exact code + branch, then code, then branch, then any.
          </p>

          {mappingsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No rules yet. Until a source has one, its money stays off the register.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Branch</th>
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2">Active</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <GroupRows
                      key={g.source}
                      label={labelFor(g.source)}
                      direction={directionFor(g.source)}
                      rules={g.rules}
                      busy={busy}
                      deleteId={deleteId}
                      onToggle={(r, active) => updateM.mutate({ id: r.id, patch: { active } })}
                      onAskDelete={setDeleteId}
                      onDelete={(id) => deleteM.mutate(id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {mappingErr(updateM.error) && (
            <p className="text-sm text-destructive">{mappingErr(updateM.error)}</p>
          )}

          <form
            className="space-y-3 rounded-md border p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit || createM.isPending) return;
              createM.mutate();
            }}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Add a rule
            </p>
            <div>
              <label className="text-xs text-muted-foreground">
                Source{' '}
                {srcMeta && (
                  <span className="text-muted-foreground/80">
                    · {directionFor(srcMeta.source)}
                    {srcMeta.branchless ? ' · no branch' : ''}
                  </span>
                )}
              </label>
              <select
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={source}
                onChange={(e) => {
                  setSourcePick(e.target.value);
                  // Code suggestions and branch eligibility are per source.
                  setCode('');
                  setAnyCode(true);
                  setFundsAccountId('');
                }}
                disabled={sources.length === 0}
              >
                {sources.map((s) => (
                  <option key={s.source} value={s.source}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Payment method code</label>
                <div className="flex items-center gap-2">
                  <Input
                    list="funds-mapping-code-suggestions"
                    value={anyCode ? '' : code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={anyCode ? 'Any code' : 'e.g. gcash'}
                    disabled={anyCode}
                    className="flex-1"
                  />
                  <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox checked={anyCode} onCheckedChange={(v) => setAnyCode(v === true)} />
                    Any code
                  </label>
                </div>
                <datalist id="funds-mapping-code-suggestions">
                  {suggestions.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.label}
                    </option>
                  ))}
                </datalist>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Free text is allowed — a retired method&apos;s history still needs a rule.
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Branch</label>
                {branchless ? (
                  <p className="flex h-9 items-center text-sm text-muted-foreground">
                    Any branch — this source carries none.
                  </p>
                ) : (
                  <select
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                    value={branchId}
                    onChange={(e) => {
                      setBranchId(e.target.value);
                      setFundsAccountId('');
                    }}
                  >
                    <option value="">Any branch</option>
                    {(branchesQ.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Post to account</label>
              <select
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={accountOk ? fundsAccountId : ''}
                onChange={(e) => setFundsAccountId(e.target.value)}
              >
                <option value="">— select —</option>
                {eligibleAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({KIND_LABEL[a.kind]}
                    {a.branchName ? ` · ${a.branchName}` : ''})
                  </option>
                ))}
              </select>
              {eligibleAccounts.length === 0 && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {branchless
                    ? 'No active company-wide account to post to. Register one without a branch first.'
                    : effectiveBranchId
                      ? 'No active account for that branch or company-wide. Register one first.'
                      : 'No active company-wide account. An "any branch" rule can only post to one.'}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Memo (optional)</label>
              <Input value={memo} onChange={(e) => setMemo(e.target.value)} />
            </div>
            {mappingErr(createM.error) && (
              <p className="text-sm text-destructive">{mappingErr(createM.error)}</p>
            )}
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!canSubmit || createM.isPending}>
                {createM.isPending ? 'Adding…' : 'Add rule'}
              </Button>
            </div>
          </form>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Done
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupRows({
  label,
  direction,
  rules,
  busy,
  deleteId,
  onToggle,
  onAskDelete,
  onDelete,
}: {
  label: string;
  direction: string;
  rules: FundsMappingRow[];
  busy: boolean;
  deleteId: string | null;
  onToggle: (r: FundsMappingRow, active: boolean) => void;
  onAskDelete: (id: string | null) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <>
      <tr className="border-b bg-muted/40">
        <td colSpan={5} className="px-3 py-1.5 text-xs font-medium">
          {label}
          <span className="ml-2 font-normal text-muted-foreground">· {direction}</span>
        </td>
      </tr>
      {rules.map((r) => (
        <tr key={r.id} className={'border-b last:border-0 ' + (r.active ? '' : 'opacity-60')}>
          <td className="px-3 py-2">
            {r.code ? (
              <span className="font-mono text-xs">{r.code}</span>
            ) : (
              <span className="text-muted-foreground">Any code</span>
            )}
          </td>
          <td className="px-3 py-2">
            {r.branchName ?? <span className="text-muted-foreground">Any branch</span>}
          </td>
          <td className="px-3 py-2">
            <span className="font-medium">{r.accountName}</span>
            <Badge variant="muted" className="ml-1.5">
              {KIND_LABEL[r.accountKind]}
            </Badge>
            {!r.accountActive && (
              <Badge variant="warning" className="ml-1.5" title="Postings will be skipped">
                Inactive account
              </Badge>
            )}
            {r.memo && <p className="text-xs text-muted-foreground">{r.memo}</p>}
          </td>
          <td className="px-3 py-2">
            <Checkbox
              checked={r.active}
              disabled={busy}
              aria-label={r.active ? 'Deactivate rule' : 'Activate rule'}
              onCheckedChange={(v) => onToggle(r, v === true)}
            />
          </td>
          <td className="px-3 py-2 text-right">
            {deleteId === r.id ? (
              <div className="flex items-center justify-end gap-1">
                <span className="text-xs text-muted-foreground">Remove?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={busy}
                  onClick={() => onDelete(r.id)}
                >
                  Yes
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onAskDelete(null)}>
                  No
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onAskDelete(r.id)}>
                Delete
              </Button>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
